import React from 'react';
import { getLinkGeometry } from '../boardDrawLinkGeometry.js';

const ARROW_MARKER_SIZE = 8;

export const BoardMapLinksLayer = ({
  autoItemLinks = [],
  links = [],
  nodeRectById,
  selectedLinkId = null,
  selectedLinkIds = [],
  onSelectLink,
  drawTool = 'select',
  lineDraft = null,
  canvasWidth,
  canvasHeight,
  renderMetrics,
  selectedLinkLabelRef = null,
  selectedLinkGeometry = null,
  promoteAutoLinkToManual,
  commitSelectedLinkLabel,
  startCurveDrag,
  resetCurveForLink,
}) => (
  <>
    <svg
      className='absolute inset-0'
      width={canvasWidth}
      height={canvasHeight}
      viewBox={`0 0 ${canvasWidth} ${canvasHeight}`}
      preserveAspectRatio='none'
      style={{ zIndex: 11, pointerEvents: 'none' }}
    >
      {autoItemLinks.map(link => {
        const geometry = getLinkGeometry(link, nodeRectById, renderMetrics);
        if (!geometry) {
          return null;
        }
        return (
          <g key={link.id}>
            <path
              d={geometry.path}
              stroke='transparent'
              strokeWidth='12'
              fill='none'
              data-testid={`map-auto-link-hit-area-${link.id}`}
              pointerEvents={drawTool === 'line' ? 'none' : 'stroke'}
              style={{ cursor: 'pointer' }}
              onPointerDown={event => {
                event.preventDefault();
                event.stopPropagation();
                promoteAutoLinkToManual?.(link);
              }}
            />
            <path
              data-testid={`map-auto-link-${link.id}`}
              d={geometry.path}
              fill='none'
              stroke='rgb(var(--contrast))'
              strokeWidth='1.6'
              strokeOpacity='0.35'
              strokeDasharray='5 4'
              style={{ pointerEvents: 'none' }}
            />
          </g>
        );
      })}
      {links.map(link => {
        const geometry = getLinkGeometry(link, nodeRectById, renderMetrics);
        if (!geometry) {
          return null;
        }
        const isSelected =
          selectedLinkId === link.id || selectedLinkIds.includes(link.id);
        const arrowToken = link?.arrows || 'none';
        const markerColor = `rgb(var(--${link?.color || 'secondary'}))`;
        const baseStrokeWidth = Number(link?.strokeWidth) || 2;
        const strokeWidth = isSelected ? Math.max(3, baseStrokeWidth) : baseStrokeWidth;
        const markerStartId = `map-arrow-start-${link.id}`;
        const markerEndId = `map-arrow-end-${link.id}`;
        return (
          <g key={link.id}>
            <defs>
              <marker
                id={markerEndId}
                markerWidth={ARROW_MARKER_SIZE}
                markerHeight={ARROW_MARKER_SIZE}
                refX='7'
                refY='4'
                orient='auto'
                markerUnits='strokeWidth'
              >
                <path d='M 0 0 L 8 4 L 0 8 z' fill={markerColor} />
              </marker>
              <marker
                id={markerStartId}
                markerWidth={ARROW_MARKER_SIZE}
                markerHeight={ARROW_MARKER_SIZE}
                refX='7'
                refY='4'
                orient='auto-start-reverse'
                markerUnits='strokeWidth'
              >
                <path d='M 0 0 L 8 4 L 0 8 z' fill={markerColor} />
              </marker>
            </defs>
            <path
              d={geometry.path}
              stroke='transparent'
              strokeWidth='12'
              fill='none'
              data-map-link-root='true'
              data-testid={`map-link-hit-area-${link.id}`}
              pointerEvents={drawTool === 'line' ? 'none' : 'stroke'}
              style={{ cursor: 'pointer' }}
              onPointerDown={event => {
                event.preventDefault();
                event.stopPropagation();
                onSelectLink?.(link.id);
              }}
            />
            <path
              d={geometry.path}
              fill='none'
              stroke={markerColor}
              strokeWidth={strokeWidth}
              strokeOpacity={isSelected ? '1' : '0.9'}
              strokeDasharray={link?.strokeDasharray || undefined}
              markerStart={
                arrowToken === 'from' || arrowToken === 'both'
                  ? `url(#${markerStartId})`
                  : undefined
              }
              markerEnd={
                arrowToken === 'to' || arrowToken === 'both'
                  ? `url(#${markerEndId})`
                  : undefined
              }
              style={{ pointerEvents: 'none' }}
            />
          </g>
        );
      })}
      {lineDraft?.path ? (
        <path
          d={lineDraft.path}
          fill='none'
          stroke='rgb(var(--secondary))'
          strokeWidth='2'
          strokeOpacity='0.95'
          strokeDasharray='4 3'
          pointerEvents='none'
        />
      ) : null}
    </svg>

    {links.map(link => {
      const geometry = getLinkGeometry(link, nodeRectById, renderMetrics);
      if (!geometry) {
        return null;
      }
      const isEditingLabel = selectedLinkId === link.id;
      if (!link?.label && !isEditingLabel) {
        return null;
      }
      return (
        <div
          key={`${link.id}-label`}
          className={`absolute ${
            isEditingLabel ? 'pointer-events-auto' : 'pointer-events-none'
          }`}
          style={{
            left: geometry.handleX,
            top: geometry.handleY - 12,
            transform: 'translate(-50%, -50%)',
            zIndex: 9997,
          }}
        >
            <span
              data-map-link-label-editor='true'
              ref={isEditingLabel ? selectedLinkLabelRef : null}
              contentEditable={isEditingLabel}
              suppressContentEditableWarning
              className={`inline-block rounded bg-main bg-opacity-85 px-03 py-px text-xs leading-4 text-contrast whitespace-nowrap ${
                isEditingLabel
                  ? 'outline-none border border-primary border-opacity-40 min-w-10'
                  : ''
              }`}
              style={{ fontSize: '0.875rem', lineHeight: '1.25rem' }}
            onPointerDown={event => {
              if (isEditingLabel) {
                event.stopPropagation();
              }
            }}
            onBlur={event => {
              if (isEditingLabel) {
                commitSelectedLinkLabel?.(event.currentTarget.textContent || '');
              }
            }}
            onKeyDown={event => {
              if (!isEditingLabel) {
                return;
              }
              if (event.key === 'Enter') {
                event.preventDefault();
                commitSelectedLinkLabel?.(event.currentTarget.textContent || '');
                event.currentTarget.blur();
              }
              if (event.key === 'Escape') {
                event.preventDefault();
                event.currentTarget.textContent = link?.label || '';
                event.currentTarget.blur();
              }
            }}
          >
            {link?.label || ''}
          </span>
        </div>
      );
    })}

    {selectedLinkGeometry ? (
      <div
        data-map-link-control-handle='true'
        data-testid='map-link-control-handle'
        className='absolute z-30 box-border cursor-grab rounded-full border border-main bg-secondary'
        style={{
          width: 14,
          height: 14,
          left: selectedLinkGeometry.handleX - 7,
          top: selectedLinkGeometry.handleY - 7,
          zIndex: 9999,
        }}
        onPointerDown={event => startCurveDrag?.(event, selectedLinkId)}
        onDoubleClick={event => {
          event.preventDefault();
          event.stopPropagation();
          resetCurveForLink?.(selectedLinkId);
        }}
      />
    ) : null}
  </>
);
